/**
 * ═══════════════════════════════════════════════════════════════════════════
 * RiskMeterTab - Tab Completo del Risk Meter
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file RiskMeterTab.tsx
 * @version 1.0.0
 * @description Tab completo (Tab 6) del Risk Meter para BulkSend.tsx.
 *              Layout de página completa con KPIs, gauge principal, grid de
 *              factores y panel de recomendaciones.
 * 
 * @component RiskMeterTab
 * @architecture Fase 4 - Tab 6 Completo del Risk Meter
 * 
 * FEATURES:
 * - Layout completo de página (no widget flotante)
 * - Header con KPIs principales
 * - RiskGauge principal grande (250px)
 * - Grid responsive de 10 factores (2 columnas)
 * - Panel de recomendaciones expandido
 * - Botón de refresh
 * - Búsqueda y filtros de factores
 * - Export de reporte (futuro)
 * 
 * SECCIONES:
 * 1. Header: KPIs + Refresh
 * 2. Main Section: Gauge + Stats rápidas
 * 3. Factors Grid: 10 factores en grid 2 columnas
 * 4. Recommendations: Panel completo
 */

import React, { useState, useMemo } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Badge, 
  Button, 
  Form,
  InputGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTachometerAlt,
  faSync,
  faDownload,
  faExclamationTriangle,
  faCheckCircle,
  faSearch,
  faFilter,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

// Importar componentes de Fase 2
import RiskGauge from './RiskGauge';
import RiskFactorCard from './RiskFactorCard';
import RecommendationPanel from './RecommendationPanel';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
type FactorStatus = 'excellent' | 'good' | 'warning' | 'danger';

interface WhatsAppRiskFactor {
  id: string;
  name: string;
  description: string;
  weight: number;
  currentValue: any;
  optimalRange: any;
  score: number;
  status: FactorStatus;
  recommendation?: string;
}

interface WhatsAppRiskAssessment {
  overallScore: number;
  riskLevel: RiskLevel;
  flagProbability: number;
  factors: WhatsAppRiskFactor[];
  recommendations: string[];
  timestamp?: number;
}

interface RiskMeterTabProps {
  /** Datos del riesgo calculado */
  riskData: WhatsAppRiskAssessment | null;
  
  /** Callback para refrescar los datos */
  onRefresh?: () => void;
  
  /** Callback para exportar reporte */
  onExport?: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════════════════════

const LEVEL_COLORS: Record<RiskLevel, string> = {
  low: '#00BDA5',      // Aqua turquesa profesional
  medium: '#FFB84D',   // Amber suave (HubSpot)
  high: '#FF7A59',     // Coral warning
  critical: '#F2545B'  // Rojo suave profesional
};

const LEVEL_BG_VARIANTS: Record<RiskLevel, string> = {
  low: 'success',
  medium: 'warning',
  high: 'warning',
  critical: 'danger'
};

const LEVEL_LABELS: Record<RiskLevel, string> = {
  low: 'Riesgo Bajo',
  medium: 'Riesgo Medio',
  high: 'Riesgo Alto',
  critical: 'Riesgo Crítico'
};

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'Todos los factores' },
  { value: 'danger', label: 'Solo peligros' },
  { value: 'warning', label: 'Solo advertencias' },
  { value: 'good', label: 'Buenos y excelentes' }
];

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export const RiskMeterTab: React.FC<RiskMeterTabProps> = ({
  riskData,
  onRefresh,
  onExport
}) => {
  // ───────────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────────
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // ───────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────────
  
  const handleRefresh = async () => {
    if (onRefresh) {
      setIsRefreshing(true);
      await onRefresh();
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };
  
  const handleExport = () => {
    if (onExport) {
      onExport();
    } else {
      // Fallback: descargar JSON
      if (riskData) {
        const dataStr = JSON.stringify(riskData, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `risk-assessment-${Date.now()}.json`;
        link.click();
        URL.revokeObjectURL(url);
      }
    }
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // FILTERED FACTORS
  // ───────────────────────────────────────────────────────────────────────
  
  const filteredFactors = useMemo(() => {
    if (!riskData) return [];
    
    let factors = riskData.factors;
    
    // Filtrar por búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      factors = factors.filter(f => 
        f.name.toLowerCase().includes(term) ||
        f.description.toLowerCase().includes(term)
      );
    }
    
    // Filtrar por status
    if (statusFilter !== 'all') {
      if (statusFilter === 'danger') {
        factors = factors.filter(f => f.status === 'danger');
      } else if (statusFilter === 'warning') {
        factors = factors.filter(f => f.status === 'warning');
      } else if (statusFilter === 'good') {
        factors = factors.filter(f => f.status === 'good' || f.status === 'excellent');
      }
    }
    
    return factors;
  }, [riskData, searchTerm, statusFilter]);
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: EMPTY STATE
  // ───────────────────────────────────────────────────────────────────────
  
  if (!riskData) {
    return (
      <div 
        style={{ 
          padding: '60px 24px',
          textAlign: 'center',
          color: 'var(--bs-secondary)',
          maxWidth: '600px',
          margin: '60px auto'
        }}
      >
        <FontAwesomeIcon 
          icon={faExclamationTriangle} 
          style={{ 
            fontSize: '4rem', 
            marginBottom: '24px',
            opacity: 0.3,
            color: 'var(--bs-secondary)'
          }}
        />
        <h4 style={{ fontWeight: 600, marginBottom: '12px' }}>
          No hay análisis de riesgo disponible
        </h4>
        <p style={{ marginBottom: '24px', lineHeight: '1.6' }}>
          Configure un mensaje y agregue contactos para calcular automáticamente
          el riesgo de su envío masivo.
        </p>
        <div 
          style={{
            padding: '20px',
            backgroundColor: 'var(--bs-body-bg)',
            border: '1px solid var(--bs-border-color)',
            borderRadius: '8px',
            textAlign: 'left'
          }}
        >
          <h6 style={{ fontWeight: 600, marginBottom: '12px' }}>
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            Pasos para comenzar:
          </h6>
          <ol style={{ marginBottom: 0, paddingLeft: '20px' }}>
            <li>Vaya al tab <strong>Composer</strong> y escriba su mensaje</li>
            <li>Vaya al tab <strong>Contacts</strong> y agregue contactos</li>
            <li>El análisis de riesgo se calculará automáticamente</li>
          </ol>
        </div>
      </div>
    );
  }
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: MAIN CONTENT
  // ───────────────────────────────────────────────────────────────────────
  
  const levelColor = LEVEL_COLORS[riskData.riskLevel];
  const levelBgVariant = LEVEL_BG_VARIANTS[riskData.riskLevel];
  const levelLabel = LEVEL_LABELS[riskData.riskLevel];
  
  return (
    <div style={{ 
      padding: '24px', 
      backgroundColor: 'var(--bs-body-bg)',
      minHeight: '100%'
    }}>
      <Container fluid>
        {/* ═════════════════════════════════════════════════════════════ */}
        {/* HEADER CON KPIS */}
        {/* ═════════════════════════════════════════════════════════════ */}
        
        <Row className="g-3">
  {/* Score General */}
  <Col md={3}>
    <Card style={{ 
      border: '1px solid rgba(0,0,0,0.06)', 
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      backgroundColor: 'var(--bs-body-bg)',
      transition: 'all 0.3s ease'
    }}>
      <Card.Body style={{ padding: '18px' }}>
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div>
            <div style={{ 
              fontSize: '0.7rem', 
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--bs-secondary)',
              marginBottom: '6px'
            }}>
              Score General
            </div>
            <div style={{ 
              fontSize: '2.25rem', 
              fontWeight: 800,
              color: levelColor,
              lineHeight: 1,
              letterSpacing: '-0.02em'
            }}>
              {Math.round(riskData.overallScore)}
            </div>
            <div style={{ 
              fontSize: '0.8rem',
              color: 'var(--bs-secondary)',
              marginTop: '4px'
            }}>
              de 100 puntos
            </div>
          </div>
          <div 
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: `${levelColor}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <FontAwesomeIcon 
              icon={faTachometerAlt} 
              style={{ fontSize: '1.25rem', color: levelColor }}
            />
          </div>
        </div>
        <div style={{ 
          height: '4px', 
          backgroundColor: '#f1f3f5', 
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${riskData.overallScore}%`,
            height: '100%',
            backgroundColor: levelColor,
            transition: 'width 0.8s ease'
          }} />
        </div>
      </Card.Body>
    </Card>
  </Col>
  
  {/* Nivel de Riesgo */}
  <Col md={3}>
    <Card style={{ 
      border: '1px solid rgba(0,0,0,0.06)', 
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      backgroundColor: 'var(--bs-body-bg)',
      transition: 'all 0.3s ease'
    }}>
      <Card.Body style={{ padding: '18px' }}>
        <div style={{ 
          fontSize: '0.7rem', 
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--bs-secondary)',
          marginBottom: '12px'
        }}>
          Nivel de Riesgo
        </div>
        <Badge 
          bg=""
          style={{ 
            fontSize: '0.875rem',
            padding: '10px 18px',
            fontWeight: 700,
            borderRadius: '10px',
            backgroundColor: levelColor,
            color: '#fff',
            boxShadow: `0 3px 10px ${levelColor}35`,
            letterSpacing: '0.02em'
          }}
        >
          {levelLabel.toUpperCase()}
        </Badge>
      </Card.Body>
    </Card>
  </Col>
  
  {/* Probabilidad Flag */}
  <Col md={3}>
    <Card style={{ 
      border: '1px solid rgba(0,0,0,0.06)', 
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      backgroundColor: 'var(--bs-body-bg)',
      transition: 'all 0.3s ease'
    }}>
      <Card.Body style={{ padding: '18px' }}>
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div>
            <div style={{ 
              fontSize: '0.7rem', 
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--bs-secondary)',
              marginBottom: '6px'
            }}>
              Probabilidad Flag
            </div>
            <div>
              <span style={{ 
                fontSize: '2.25rem', 
                fontWeight: 800,
                color: levelColor,
                lineHeight: 1,
                letterSpacing: '-0.02em'
              }}>
                {Math.round(riskData.flagProbability)}
              </span>
              <span style={{ 
                fontSize: '1.25rem',
                fontWeight: 600,
                color: levelColor
              }}>
                %
              </span>
            </div>
            <div style={{ 
              fontSize: '0.8rem',
              color: 'var(--bs-secondary)',
              marginTop: '4px'
            }}>
              estimada
            </div>
          </div>
          <div 
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: `${levelColor}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <FontAwesomeIcon 
              icon={faExclamationTriangle} 
              style={{ fontSize: '1.25rem', color: levelColor }}
            />
          </div>
        </div>
      </Card.Body>
    </Card>
  </Col>
  
  {/* Factores Evaluados */}
  <Col md={3}>
    <Card style={{ 
      border: '1px solid rgba(0,0,0,0.06)', 
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      backgroundColor: 'var(--bs-body-bg)',
      transition: 'all 0.3s ease'
    }}>
      <Card.Body style={{ padding: '18px' }}>
        <div className="d-flex align-items-start justify-content-between">
          <div>
            <div style={{ 
              fontSize: '0.7rem', 
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--bs-secondary)',
              marginBottom: '6px'
            }}>
              Factores Evaluados
            </div>
            <div style={{ 
              fontSize: '2.25rem', 
              fontWeight: 800,
              color: 'var(--bs-body-color)',
              lineHeight: 1,
              letterSpacing: '-0.02em'
            }}>
              {riskData.factors.length}
            </div>
            <div style={{ 
              fontSize: '0.8rem',
              color: 'var(--bs-secondary)',
              marginTop: '4px'
            }}>
              criterios
            </div>
          </div>
          <div 
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: '#0091AE15',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <FontAwesomeIcon 
              icon={faCheckCircle} 
              style={{ fontSize: '1.25rem', color: '#0091AE' }}
            />
          </div>
        </div>
      </Card.Body>
    </Card>
  </Col>
</Row>
        
        {/* ═════════════════════════════════════════════════════════════ */}
        {/* MAIN GAUGE SECTION */}
        {/* ═════════════════════════════════════════════════════════════ */}
        
        <Row className="mb-4">
          <Col lg={5}>
            <Card style={{ 
              border: 'none', 
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              backgroundColor: 'var(--bs-body-bg)',
              padding: '32px',
              textAlign: 'center'
            }}>
              <h5 style={{ fontWeight: 600, marginBottom: '24px' }}>
                Medidor de Riesgo Principal
              </h5>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
                <RiskGauge
                  score={riskData.overallScore}
                  level={riskData.riskLevel}
                  size={250}
                  animated={true}
                />
              </div>
              <div 
                style={{
                  padding: '16px',
                  backgroundColor: `${levelColor}10`,
                  border: `1px solid ${levelColor}40`,
                  borderRadius: '8px'
                }}
              >
                <p style={{ marginBottom: 0, fontSize: '0.95rem', lineHeight: '1.5' }}>
                  {riskData.riskLevel === 'low' && (
                    <>
                      <strong style={{ color: levelColor }}>✓ Excelente configuración.</strong> Su envío tiene
                      alta probabilidad de éxito sin flags de WhatsApp.
                    </>
                  )}
                  {riskData.riskLevel === 'medium' && (
                    <>
                      <strong style={{ color: levelColor }}>⚠ Configuración aceptable.</strong> Considere
                      aplicar algunas recomendaciones para reducir el riesgo.
                    </>
                  )}
                  {riskData.riskLevel === 'high' && (
                    <>
                      <strong style={{ color: levelColor }}>⚠ Riesgo elevado.</strong> Es altamente
                      recomendable aplicar las mejoras sugeridas antes de enviar.
                    </>
                  )}
                  {riskData.riskLevel === 'critical' && (
                    <>
                      <strong style={{ color: levelColor }}>🔴 Riesgo crítico.</strong> No se recomienda
                      enviar sin aplicar correcciones. Alto riesgo de flag.
                    </>
                  )}
                </p>
              </div>
            </Card>
          </Col>
          
          <Col lg={7}>
            <Card style={{ 
              border: 'none', 
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              backgroundColor: 'var(--bs-body-bg)',
              height: '100%'
            }}>
              <Card.Header style={{
                backgroundColor: 'var(--bs-body-bg)',
                borderBottom: '1px solid var(--bs-border-color)',
                padding: '16px 20px'
              }}>
                <h5 className="mb-0" style={{ fontWeight: 600 }}>
                  Recomendaciones Prioritarias
                </h5>
              </Card.Header>
              <Card.Body style={{ padding: '20px' }}>
                <RecommendationPanel
                  recommendations={riskData.recommendations.slice(0, 5)}
                  title=""
                  showActions={false}
                  maxHeight="350px"
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
        
        {/* ═════════════════════════════════════════════════════════════ */}
        {/* FACTORS SECTION */}
        {/* ═════════════════════════════════════════════════════════════ */}
        
        <Row className="mb-4">
          <Col>
            <Card style={{ 
              border: 'none', 
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              backgroundColor: 'var(--bs-body-bg)'
            }}>
              <Card.Header style={{
                backgroundColor: 'var(--bs-body-bg)',
                borderBottom: '1px solid var(--bs-border-color)',
                padding: '16px 20px'
              }}>
                <Row className="align-items-center">
                  <Col>
                    <h5 className="mb-0" style={{ fontWeight: 600 }}>
                      Factores de Riesgo Detallados
                      <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.8rem' }}>
                        {filteredFactors.length} de {riskData.factors.length}
                      </Badge>
                    </h5>
                  </Col>
                  <Col md={6}>
                    <Row className="g-2">
                      <Col md={7}>
                        <InputGroup size="sm">
                          <InputGroup.Text style={{ backgroundColor: 'var(--bs-body-bg)' }}>
                            <FontAwesomeIcon icon={faSearch} />
                          </InputGroup.Text>
                          <Form.Control
                            placeholder="Buscar factor..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ backgroundColor: 'var(--bs-body-bg)' }}
                          />
                        </InputGroup>
                      </Col>
                      <Col md={5}>
                        <InputGroup size="sm">
                          <InputGroup.Text style={{ backgroundColor: 'var(--bs-body-bg)' }}>
                            <FontAwesomeIcon icon={faFilter} />
                          </InputGroup.Text>
                          <Form.Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{ backgroundColor: 'var(--bs-body-bg)' }}
                          >
                            {STATUS_FILTER_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </Form.Select>
                        </InputGroup>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Card.Header>
              
              <Card.Body style={{ padding: '20px' }}>
                {filteredFactors.length === 0 ? (
                  <div style={{ 
                    padding: '40px', 
                    textAlign: 'center',
                    color: 'var(--bs-secondary)'
                  }}>
                    <FontAwesomeIcon icon={faSearch} style={{ fontSize: '2rem', marginBottom: '12px', opacity: 0.3 }} />
                    <p>No se encontraron factores con los filtros aplicados.</p>
                  </div>
                ) : (
                  <Row className="g-3">
                    {filteredFactors.map((factor) => (
                      <Col key={factor.id} lg={6}>
                        <RiskFactorCard
                          factor={factor}
                          showWeight={true}
                        />
                      </Col>
                    ))}
                  </Row>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        
        {/* ═════════════════════════════════════════════════════════════ */}
        {/* FOOTER INFO */}
        {/* ═════════════════════════════════════════════════════════════ */}
        
        <Row>
          <Col>
            <Card style={{  
              borderRadius: '12px',
              backgroundColor: '#3b82f610',
              border: '1px solid #3b82f640'
            }}>
              <Card.Body style={{ padding: '16px 20px' }}>
                <div className="d-flex align-items-center gap-3">
                  <FontAwesomeIcon 
                    icon={faInfoCircle}
                    style={{ fontSize: '1.5rem', color: '#3b82f6' }}
                  />
                  <div style={{ flex: 1 }}>
                    <strong>Acerca del Risk Meter:</strong> Este análisis evalúa 10 factores críticos
                    que afectan la probabilidad de que WhatsApp marque su envío como spam. Los factores
                    incluyen: mensajes por hora, tamaño de lista, longitud del mensaje, emojis, enlaces,
                    variables personalizadas, horarios de operación, días de envío, primera respuesta IA
                    y estado de WhatsApp.
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT
// ═══════════════════════════════════════════════════════════════════════════

export default RiskMeterTab;
